import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { SUPPLIER_CATEGORIES, labelFor } from "@/lib/constants";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, X, Plus } from "lucide-react";

const catLabel = (v) => labelFor(v, SUPPLIER_CATEGORIES);

export default function CategoryMultiSelect({ value = [], onChange, testid = "category-multiselect" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [custom, setCustom] = useState([]); // custom categories used elsewhere (global reuse)

  useEffect(() => {
    api.get("/supplier-categories").then((r) => setCustom(r.data.categories || [])).catch(() => {});
  }, []);

  const options = useMemo(() => {
    const map = new Map();
    SUPPLIER_CATEGORIES.forEach((c) => map.set(c.value, c.label));
    custom.forEach((v) => { if (!map.has(v)) map.set(v, v); });
    value.forEach((v) => { if (!map.has(v)) map.set(v, catLabel(v)); });
    return Array.from(map, ([val, label]) => ({ value: val, label }));
  }, [custom, value]);

  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };

  const create = () => {
    const v = query.trim();
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    if (!custom.includes(v) && !SUPPLIER_CATEGORIES.some((c) => c.value === v)) setCustom((c) => [...c, v]);
    setQuery("");
  };

  const q = query.trim().toLowerCase();
  const filtered = options.filter((o) => o.label.toLowerCase().includes(q));
  const exactExists = options.some((o) => o.label.toLowerCase() === q || o.value.toLowerCase() === q);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="flex min-h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-1.5 text-sm shadow-sm hover:border-primary/60 focus:outline-none focus:ring-1 focus:ring-ring"
        >
          <span className="flex flex-wrap items-center gap-1">
            {value.length ? value.map((v) => (
              <span key={v} className="inline-flex items-center gap-1 rounded-full bg-[#E6ECF5] px-2 py-0.5 text-xs font-medium text-[#1B4383]" data-testid={`${testid}-chip-${v}`}>
                {catLabel(v)}
                <X className="h-3 w-3 cursor-pointer hover:text-destructive" onClick={(e) => { e.stopPropagation(); toggle(v); }} />
              </span>
            )) : <span className="text-muted-foreground">Select or type categories…</span>}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search or add a category…" value={query} onValueChange={setQuery} data-testid={`${testid}-input`} />
          <CommandList>
            {!filtered.length && !query.trim() && <CommandEmpty>No categories.</CommandEmpty>}
            {query.trim() && !exactExists && (
              <CommandGroup>
                <CommandItem value={`__create__${query}`} onSelect={create} data-testid={`${testid}-create`}>
                  <Plus className="mr-2 h-4 w-4" />Add “{query.trim()}”
                </CommandItem>
              </CommandGroup>
            )}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((o) => (
                  <CommandItem key={o.value} value={o.value} onSelect={() => toggle(o.value)} data-testid={`${testid}-option-${o.value}`}>
                    <Check className={`mr-2 h-4 w-4 ${value.includes(o.value) ? "opacity-100" : "opacity-0"}`} />
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
